import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Coins, Activity, Gauge, Cpu, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";

const Card = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-muted-foreground"><Icon className="h-4 w-4 text-gold-400" /><span className="text-xs uppercase tracking-wider">{label}</span></div>
    <p className="mt-2 font-display text-3xl font-bold text-gold-400">{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

export default function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/stats/overview").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="grid h-64 place-items-center text-muted-foreground">Chargement…</div>;

  return (
    <div className="space-y-6" data-testid="overview-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold-400">Aperçu</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight sm:text-4xl">Votre consommation</h1>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card icon={Coins} label="Solde" value={d.balance.toLocaleString("fr-FR")} sub="crédits disponibles" testid="stat-balance" />
        <Card icon={TrendingUp} label="Ce mois-ci" value={d.credits_month.toLocaleString("fr-FR")} sub="crédits consommés" testid="stat-month" />
        <Card icon={Activity} label="Requêtes" value={d.requests_total.toLocaleString("fr-FR")} sub="appels au total" testid="stat-requests" />
        <Card icon={Gauge} label="Latence moy." value={d.avg_latency_ms ? `${d.avg_latency_ms} ms` : "—"} sub="temps de réponse" testid="stat-latency" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5 lg:col-span-2" data-testid="overview-chart">
          <h3 className="mb-4 font-display font-semibold">Crédits consommés — 7 derniers jours</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="day" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0A0F24", border: "1px solid #1E294B", borderRadius: 12, color: "#F8FAFC" }} cursor={{ fill: "rgba(212,175,55,0.08)" }} />
              <Bar dataKey="credits" fill="#D4AF37" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="overview-top-model">
            <div className="flex items-center gap-2 text-muted-foreground"><Cpu className="h-4 w-4 text-gold-400" /><span className="text-xs uppercase tracking-wider">Modèle le plus utilisé</span></div>
            <p className="mt-2 font-display text-xl font-bold">{d.top_model || "—"}</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="mb-3 font-display font-semibold">Répartition par type</h3>
            <div className="space-y-2">
              {d.by_kind.length === 0 && <p className="text-sm text-muted-foreground">Aucune consommation.</p>}
              {d.by_kind.map((k) => (
                <div key={k.kind} className="flex items-center justify-between text-sm">
                  <span className="capitalize text-muted-foreground">{k.kind}</span>
                  <span className="font-mono text-gold-400">{k.credits} cr.</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
